import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import {
  SubtypeBreakdown,
  WorkItemSubtype,
} from "../../../store/services/salesManagerService";
import {
  getWorkItemColor,
  getWorkItemIcon,
} from "../../../utils/workItemUtils";
import {
  formatPercent,
  getSubtypeCounts,
  SUBTYPE_LABELS,
  SUBTYPE_ORDER,
  SUBTYPE_TO_WORK_ITEM_TYPE,
} from "../shared/breakdown";

interface SubtypeProgressListProps {
  /** En specifik SubtypeBreakdown — t.ex. Breakdown.Total.Communication för
   * att visa bara Planering, eller Breakdown.Total.Followup för Mötesbokning. */
  subtypeBreakdown: SubtypeBreakdown;
  /** Vilka subtyper som ska visas och i vilken ordning. Default = alla. */
  subtypeOrder?: WorkItemSubtype[];
}

/**
 * Lista med en progress-bar per aktivitetstyp (Email, Telefon, SMS, Digital
 * möte, Fysiskt möte, Övrigt). Syftet är att undvika missförståndet som
 * SubtypeBarChart kan ge — där en stor "Fysiskt möte"-stapel lätt tolkas
 * som att mötet har ägt rum, även om det bara är planerat.
 *
 * Komponenten är kategori-agnostisk: anroparen väljer vilken SubtypeBreakdown
 * som ska visas (Communication/Planering, Followup/Mötesbokning, eller
 * Total). Detta gör att drawern kan visa Planering och Mötesbokning som
 * separata listor istället för att mixa dem.
 *
 * Layout:
 * - Ikon + namn till vänster
 * - "X / Y" + procent till höger
 * - Horisontell bar under: grön (slutförda) till vänster, röd (overdue)
 *   till höger, grå (aktiva på-spår) i mitten.
 */
const SubtypeProgressList: React.FC<SubtypeProgressListProps> = ({
  subtypeBreakdown,
  subtypeOrder = SUBTYPE_ORDER,
}) => {
  const theme = useTheme();

  const rows = useMemo(
    () =>
      subtypeOrder.map((subtype) => {
        const counts = getSubtypeCounts(subtypeBreakdown, subtype);
        const workItemType = SUBTYPE_TO_WORK_ITEM_TYPE[subtype];
        return {
          subtype,
          label: SUBTYPE_LABELS[subtype],
          counts,
          icon: getWorkItemIcon(workItemType),
          color: getWorkItemColor(workItemType),
        };
      }).filter((row) => row.counts.Total > 0),
    [subtypeBreakdown, subtypeOrder]
  );

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Inga aktiviteter att visa
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {rows.map(({ subtype, label, counts, icon, color }) => {
        const skipped = counts.Skipped ?? 0;
        const genuineCompleted = counts.Completed - skipped;
        const completedPct =
          counts.Total > 0 ? (genuineCompleted / counts.Total) * 100 : 0;
        const skippedPct =
          counts.Total > 0 ? (skipped / counts.Total) * 100 : 0;
        const overduePct =
          counts.Total > 0 ? (counts.Overdue / counts.Total) * 100 : 0;
        // Middle grey = aktiva på-spår (inte overdue). Beräknas implicit
        // som allt mellan completed och overdue.

        return (
          <Box key={subtype}>
            {/* Rad-header: ikon + namn + antal + procent */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 0.75 }}
            >
              <Box
                sx={{
                  color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                }}
              >
                {icon}
              </Box>
              <Typography
                variant="body2"
                sx={{ flex: 1, fontWeight: 500 }}
                noWrap
              >
                {label}
              </Typography>
              <Stack alignItems="flex-end" sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  {counts.Completed} / {counts.Total}
                </Typography>
                {counts.Skipped > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.disabled,
                      lineHeight: 1.1,
                    }}
                  >
                    varav {counts.Skipped} skippade
                  </Typography>
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 44, textAlign: "right" }}
              >
                {formatPercent(counts.CompletionRate, 0)}
              </Typography>
            </Stack>

            {/* Segmenterad progress-bar: grön (slutförda) + grå (aktiva
                 på-spår, implicit mitten) + röd (overdue, höger). */}
            <Box
              sx={{
                position: "relative",
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                overflow: "hidden",
              }}
            >
              {completedPct > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${completedPct}%`,
                    bgcolor: theme.palette.success.main,
                  }}
                />
              )}
              {skippedPct > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${completedPct}%`,
                    top: 0,
                    height: "100%",
                    width: `${skippedPct}%`,
                    bgcolor: alpha(theme.palette.success.main, 0.35),
                  }}
                />
              )}
              {overduePct > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    width: `${overduePct}%`,
                    bgcolor: theme.palette.error.main,
                  }}
                />
              )}
            </Box>

            {/* Under-rad med breakdown av status */}
            {(counts.Active > 0 || counts.Overdue > 0 || counts.Skipped > 0) && (
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                {counts.Active - counts.Overdue > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {counts.Active - counts.Overdue} aktiva
                  </Typography>
                )}
                {counts.Overdue > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.error.main, fontWeight: 500 }}
                  >
                    {counts.Overdue} overdue
                  </Typography>
                )}
                {counts.Skipped > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.disabled }}
                  >
                    {counts.Skipped} skippade
                  </Typography>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};

export default SubtypeProgressList;
